/**
 * `ConfirmDialog` — modal confirmation for destructive actions
 * (disabling an account, saving billing). Renders a backdrop +
 * centered card with `Cancel` / `Confirm` buttons. `tone='coral'`
 * for destructive, `tone='lime'` for non-destructive defaults.
 *
 * Pure render — does no state itself; the parent controls `open`
 * and the resolve/reject of the user's choice via `onConfirm` /
 * `onCancel`.
 */

import type { ReactNode } from 'react';

import { useC } from '../../lib/theme.js';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

export type ConfirmTone = 'coral' | 'lime';

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: ConfirmTone;
  pending?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'coral',
  pending = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps): JSX.Element | null {
  const C = useC();
  if (!open) return null;

  const confirmBg = tone === 'coral' ? C.coralFg : C.lime;
  const confirmFg = tone === 'coral' ? C.text : C.ink;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ background: 'rgba(0,0,0,0.55)' }}
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: C.surface,
          border: `1px solid ${C.border}`,
          borderRadius: 3,
          width: '100%',
          maxWidth: 440,
          padding: 24,
        }}
      >
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.muted,
            letterSpacing: 2,
            textTransform: 'uppercase',
            marginBottom: 8,
          }}
        >
          Confirm
        </div>
        <h2
          style={{
            fontFamily: sansFont,
            fontSize: 18,
            fontWeight: 500,
            color: C.text,
            marginBottom: body !== undefined ? 12 : 24,
          }}
        >
          {title}
        </h2>
        {body !== undefined ? (
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              color: C.body,
              lineHeight: 1.5,
              marginBottom: 24,
            }}
          >
            {body}
          </div>
        ) : null}
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={pending}
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              color: C.muted,
              background: 'transparent',
              border: `1px solid ${C.border}`,
              padding: '8px 14px',
              borderRadius: 2,
            }}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={pending}
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              fontWeight: 600,
              color: confirmFg,
              background: confirmBg,
              border: 0,
              padding: '8px 14px',
              borderRadius: 2,
              opacity: pending ? 0.6 : 1,
            }}
          >
            {pending ? 'Saving…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
